import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import moment from "moment";

//Components
import { Pill, IconButton, SearchInput, Button, useTimeFrameComponent, Tooltip, Checkbox } from '@gpstrackit/gtc-react-controls';
import Modal, { ModalBody, ModalFooter } from "Components/Modal";
import { Alert, Grid, Icon } from '@mui/material';
import TableView from 'Components/TableView';
import { Timezone } from '../../../../components/uiControls'

//Views
import WarningDetails from 'Modules/.common/components/Warnings/.components/Details';
import ConfirmResolve from 'Modules/.common/components/Warnings/.components/ConfirmResolve';
import ResolveWarning from 'Modules/.common/components/Warnings/.components/Resolve';
import DeleteWarning from 'Modules/.common/components/Warnings/.components/Delete';

//Actions
import {
    openWarningConfirmResolveModal,
    openWarningDetailsModal,
    openWarningResolveModal,
    openDeleteWarningsModal
} from 'Redux/actions';
import {
    searchGroupedMaintenanceWarningEvents,
    getUnitOfflineThreshold,
    updateWarningsItems,
    reset,
} from './redux/slice';

//Helpers
import { checkDeviceData } from 'Core/data/Helpers';
import { HasPermission } from "Core/security";

const Warnings = (props) => {
    const dispatch = useDispatch(0)
    const { messages, locale } = useIntl();
    const tableRef = useRef();

    //Redux States
    const resultResolveMaintenanceWarningEvent = useSelector(state => state.warningsConfirmResolveRedux.result);
    const { open, loading, data, items, total, offset, limit, offlineThresholdMins } = useSelector(state => state.warningsRedux);
    const openModalConfirmResolveWarnings = useSelector(state => state.warningsConfirmResolveRedux.open);
    const resultDeleteMaintenanceWarningEvent = useSelector(state => state.warningsDeleteRedux.result);
    const openModalResolveWarnings = useSelector(state => state.warningsResolveRedux.open);
    const openModalWarningDetails = useSelector(state => state.warningsDetailsRedux.open);
    const openModalDeleteWarnings = useSelector(state => state.warningsDeleteRedux.open);
    const dateformat = useSelector(state => state.securityRedux.user.dateformat);
    const user = useSelector((state) => state.securityRedux.user);

    //Local States
    const [sdCardAlert, setSdCardAlert] = useState(null);
    const [openSearch, setOpenSearch] = useState(null);
    const [search, setSearch] = useState(false);
    const [type, setType] = useState("issues");
    const [page, setPage] = useState(0);
    const [resolved, setResolved] = useState(0);

    const timeFromDate = (date, language = 'en') => {
        moment.locale(language);
        const dateMoment = moment(date);
        const timeElapsed = dateMoment.fromNow();
        return timeElapsed;
    }

    const timeFrameOptions = [
        { id: "custom", label: messages['date_custom'] },
        { id: "today", label: messages['date_today'] },
        { id: "yesterday", label: messages['date_yesterday'] },
        { id: "week", label: messages['date_this_week'] },
        { id: "lastWeek", label: messages['date_last_week'] },
        { id: "thisMonth", label: messages['date_this_month'] },
        { id: "lastMonth", label: messages['date_last_month'] },
        { id: "quarter", label: messages['date_this_quarter'] },
        { id: "lastQuarter", label: messages['date_last_quarter'] },
    ]

    const timeFrame = useTimeFrameComponent(timeFrameOptions)

    const types = [
        { id: "issues", label: messages['warnings_main_issues'], color: '#66bb6a', },
        { id: "resolved", label: messages['warnings_main_resolved'], color: '#f1f1f1', }
    ];

    const handleClose = () => {
        dispatch(reset())
        setSdCardAlert(null);
        setSearch(false)
    }

    const getDateRange = () => {
        const { startDate = moment().startOf('day'), endDate = moment().endOf('day') } = timeFrame || {};
        return {
            start: moment.utc(startDate).format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z',
            end: moment.utc(endDate).format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z'
        };
    }

    const tabsHandler = (type) => {
        setPage(0);
        dispatch(reset({ items: [], total: 0, offset: 0 }))
        let currentState = type === "resolved" ? true : false
        setResolved(currentState)
        setType(type)
        dispatch(searchGroupedMaintenanceWarningEvents({ unitId: data?.id, openSearch: openSearch, offset: 0, resolved: currentState }))
    }

    const onChangePage = (paramPage) => {
        setPage(paramPage);
        try {
            setSearch(true)
            let currentOffset = paramPage === 1 ? 0 : offset + limit
            dispatch(reset({ offset: currentOffset }))
            const dateRange = getDateRange();
            dispatch(searchGroupedMaintenanceWarningEvents({ unitId: data?.id, openSearch: openSearch, offset: currentOffset, dateRange, resolved }))
        } catch (exc) { }
    };

    useEffect(() => {
        if (open) {
            dispatch(getUnitOfflineThreshold({ id: data?.id }))
        }
        if (open && data.sdCard) {
            try {
                let sdCard = checkDeviceData(data?.sdCard)
                if (sdCard?.status && sdCard?.status?.toUpperCase() !== "NORMAL") {
                    setSdCardAlert(sdCard)
                }
            } catch (exc) { }
        }
    }, [open])

    useEffect(() => {
        if (openSearch || openSearch === "") {
            try {
                const timer = setTimeout(() => {
                    const dateRange = getDateRange();
                    dispatch(reset({ items: [], total: 0, offset: 0 }))
                    let currentOpenSearch = openSearch.replace(/\s/g, '');
                    currentOpenSearch = currentOpenSearch === "SDCardError" ? "SDCardStatus" : currentOpenSearch
                    dispatch(searchGroupedMaintenanceWarningEvents({ unitId: data?.id, openSearch: currentOpenSearch, offset: 0, dateRange, resolved }))
                }, 500)
                return () => clearTimeout(timer);
            } catch (exc) { }
        }
    }, [openSearch])

    useEffect(() => {
        if (search) {
            dispatch(reset({ items: [], total: 0, offset: 0 }))
            const dateRange = getDateRange();
            dispatch(searchGroupedMaintenanceWarningEvents({ unitId: data?.id, openSearch: openSearch, offset: 0, dateRange, resolved }))
        }
    }, [timeFrame?.startDate, timeFrame?.endDate])

    useEffect(() => {
        if (resultResolveMaintenanceWarningEvent?.resolve || resultDeleteMaintenanceWarningEvent?.delete) {
            dispatch(reset({ items: [], total: 0, offset: 0 }))
            const dateRange = getDateRange();
            dispatch(searchGroupedMaintenanceWarningEvents({ unitId: data?.id, openSearch: openSearch, offset: 0, dateRange, resolved }))
        }
    }, [resultResolveMaintenanceWarningEvent, resultDeleteMaintenanceWarningEvent])

    const timeAgo = (mins, lang = 'en') => {
        const minutes = mins % 60;
        const hours = Math.floor((mins / 60) % 24);
        const days = Math.floor(mins / 1440);

        let result = '';

        if (days > 0) result += `${days} ${days > 1 ? messages['days'] : messages['day']}, `;
        if (hours > 0) result += `${hours} ${hours > 1 ? messages['hours'] : messages['hour']}, `;
        if (minutes > 0) result += `${minutes} ${minutes > 1 ? messages['mins'] : messages['min']} `;

        if (lang === 'es') {
            return `${messages['ago']} ${result.trim()}`;
        } else {
            return `${result.trim()} ${messages['ago']}`;
        }
    }

    return (
        <>
            <Modal
                title={`${messages['warnings_main_warnings']} (${data?.label || ''})`}
                position="right"
                size="small"
                open={open}
                loading={loading}
                handleClose={() => handleClose()}
            >
                <ModalBody>
                    <div style={{ overflow: 'hidden', padding: '0px 25px' }}>
                        {
                            sdCardAlert &&
                            <Alert
                                style={{ display: 'flex', alignItems: 'center', height: 30, fontSize: 11 }}
                                severity="error"
                                icon={<Icon style={{ color: '#F44336' }}>cancel</Icon>}
                            >
                                <div style={{ display: 'grid' }}>
                                    <span>{messages['warnings_main_sd_card_status_is']}  <strong>{sdCardAlert?.status}</strong></span>
                                    {
                                        sdCardAlert?.updatedOn &&
                                        <span className="maintenance-warnings-timezone">{messages['warnings_main_last_status']}:
                                            <Timezone
                                                date={new Date(sdCardAlert?.updatedOn)}
                                                dateFormat={dateformat}
                                                timeFormat="hh:mm a"
                                                timezone={data?.timeZone?.name || "UTC"}
                                                className="maintenance-warnings-date"
                                                customFormat={true}
                                                locale={locale}
                                                noWrap={false}
                                                oneControl={true}
                                            />
                                        </span>
                                    }
                                </div>
                            </Alert>
                        }
                        {
                            !sdCardAlert && data?.isOffline && offlineThresholdMins ?
                                <Alert
                                    style={{ display: 'flex', alignItems: 'center', height: locale === 'en' ? 40 : 50 }}
                                    severity="error"
                                    color="warning"
                                    icon={<Icon style={{ color: '#ffdc00' }}>warning</Icon>}
                                >
                                    {
                                        `${messages['warnings_main_first_message']} ${timeAgo(offlineThresholdMins, locale)}. ${messages['warnings_main_second_message']}.`
                                    }
                                </Alert> : <></>
                        }
                        <Grid container spacing={1} style={{ marginTop: 5 }}>
                            {types.map((filter, index) => {
                                return (
                                    <Grid item xs={6} key={index} className="maintenance-warnings-pills">
                                        <Pill
                                            key={index}
                                            textField={'label'}
                                            item={{ ...filter, color: type === filter.id ? "#66bb6a" : "#f1f1f1" }}
                                            onClick={() => tabsHandler(filter.id)}
                                            style={{ border: '0', width: '100%', textAlign: 'center' }}
                                            label="pill-label-text"
                                            size="filled"
                                        />
                                    </Grid>
                                )
                            })}
                            <Grid item xs={12}>
                                <div className="maintenance-warnings-input-search" style={{ marginBottom: 5 }}>
                                    <SearchInput
                                        searchText={openSearch}
                                        onSearchText={(value) => setOpenSearch(value)}
                                        icon="search"
                                        placeholder={messages['warnings_main_search_by_warning']}
                                    />
                                </div>
                            </Grid>
                            {
                                items?.length > 0 ? (
                                    <Grid item xs={12}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px', marginTop: 5 }}>
                                            <div>
                                                {
                                                    items.some((item) => item.checked) &&
                                                    <a onClick={() => {
                                                        let selected = items.filter((item) => item.checked).map((item) => (item?.warning))
                                                        if (!resolved) {
                                                            dispatch(openWarningConfirmResolveModal({ unitId: data?.id, unitLabel: data?.label, warnings: selected }))
                                                        } else {
                                                            dispatch(openDeleteWarningsModal({ unitId: data?.id, unitLabel: data?.label, warnings: selected }))
                                                        }

                                                    }} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                                        {resolved ? HasPermission(user, "wgdl", [279]) ? messages['warnings_main_delete_selected'] : '' : HasPermission(user, "wgrd", [280]) ? messages['warnings_main_revolve_selected'] : ''}
                                                    </a>
                                                }
                                            </div>
                                            <div>
                                                <a onClick={() => {
                                                    let selected = items.map((item) => (item?.warning))
                                                    if (!resolved) {
                                                        dispatch(openWarningConfirmResolveModal({ unitId: data?.id, unitLabel: data?.label, warnings: selected, bulk: true }))
                                                    } else {
                                                        dispatch(openDeleteWarningsModal({ unitId: data?.id, unitLabel: data?.label, warnings: selected, bulk: true }))
                                                    }
                                                }} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                                    {resolved ? HasPermission(user, "wgdl", [279]) ? messages['warnings_main_clear_all'] : '' : HasPermission(user, "wgrd", [280]) ? messages['warnings_main_resolve_all'] : ''}
                                                </a>
                                            </div>
                                        </div>
                                    </Grid>
                                ) : <></>
                            }
                            <Grid item xs={12}>
                                <TableView
                                    ref={tableRef}
                                    data={items || []}
                                    loading={loading}
                                    page={page}
                                    rowsPerPage={20}
                                    name="maintenance-warnings-list"
                                    topSpaceSize={300}
                                    infiniteScroll={true}
                                    viewType="list"
                                    messages={messages}
                                    itemSelector={false}
                                    serverSide
                                    onChangePage={(newPage) => onChangePage(newPage)}
                                    total={total}
                                    moreRowsAvailable={items?.length < total}
                                    renderRow={(item) => {
                                        return (
                                            <div className="maintenance-warnings-card">
                                                <Grid container>
                                                    <Grid item xs={11}>
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            {
                                                                (resolved && HasPermission(user, "wgdl", [279]) || !resolved && HasPermission(user, "wgrd", [280])) &&
                                                                <Checkbox
                                                                    checked={item?.checked || false}
                                                                    onChange={(value) => {
                                                                        let currentItems = [...items];
                                                                        let index = currentItems.findIndex(warning => warning.warning === item?.warning && warning.firstEventDate === item?.firstEventDate);
                                                                        if (index !== -1) {
                                                                            let updatedItem = { ...currentItems[index], checked: value };
                                                                            currentItems[index] = updatedItem;
                                                                            dispatch(updateWarningsItems(currentItems));
                                                                        }
                                                                    }}
                                                                />
                                                            }
                                                            <Tooltip title={(!resolved && HasPermission(user, "wgrd", [280])) ? messages['warnings_main_revolve_issue'] : (resolved && HasPermission(user, "wgdl", [279])) ? messages['warnings_main_delete'] : ''}>
                                                                <span
                                                                    style={{ cursor: 'pointer', fontWeight: 700, fontSize: 16 }}
                                                                    onClick={() => {
                                                                        if (!resolved && HasPermission(user, "wgrd", [280])) {
                                                                            dispatch(openWarningResolveModal({
                                                                                unitId: data?.id,
                                                                                unitLabel: data?.label,
                                                                                warning: item?.warning,
                                                                                timezone: data?.timeZone?.name,
                                                                                firstEventDate: item.firstEventDate,
                                                                                lastEventDate: item.lastEventDate
                                                                            }))
                                                                        } else if (resolved && HasPermission(user, "wgdl", [279])) {
                                                                            dispatch(openDeleteWarningsModal({
                                                                                unitId: data?.id,
                                                                                unitLabel: data?.label,
                                                                                warnings: [item?.warning]
                                                                            }))
                                                                        }
                                                                    }}
                                                                >{messages[`alertsNewAlert_${item?.warning}`] || item?.warning}</span>
                                                            </Tooltip>
                                                        </div>
                                                        <div>
                                                            <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['warnings_main_first_occurrence']}:</label>
                                                            <span>
                                                                {
                                                                    item?.firstEventDate &&
                                                                    <Timezone
                                                                        date={new Date(item?.firstEventDate)}
                                                                        dateFormat={dateformat}
                                                                        timeFormat="hh:mm a"
                                                                        timezone={data?.timeZone?.name || "UTC"}
                                                                        className="maintenance-warnings-date"
                                                                        customFormat={true}
                                                                        locale={locale}
                                                                        noWrap={false}
                                                                        oneControl={true}
                                                                    />
                                                                }
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['warnings_main_last_occurrence']}:</label>
                                                            <span>
                                                                {
                                                                    item?.lastEventDate &&
                                                                    <>
                                                                        <Timezone
                                                                            date={new Date(item?.lastEventDate)}
                                                                            dateFormat={dateformat}
                                                                            timeFormat="hh:mm a"
                                                                            timezone={data?.timeZone?.name || "UTC"}
                                                                            className="maintenance-warnings-date"
                                                                            customFormat={true}
                                                                            locale={locale}
                                                                            noWrap={false}
                                                                            oneControl={true}
                                                                        />
                                                                        {` (${timeFromDate(item.lastEventDate, locale)})`}
                                                                    </>
                                                                }
                                                            </span>
                                                        </div>
                                                        {
                                                            (resolved && item?.resolvedOn) ?
                                                                <div>
                                                                    <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['warnings_main_resolved_on']}:</label>
                                                                    <span>
                                                                        {
                                                                            item.resolvedOn &&
                                                                            <Timezone
                                                                                date={new Date(item.resolvedOn)}
                                                                                dateFormat={dateformat}
                                                                                timeFormat="hh:mm a"
                                                                                timezone={data?.timeZone?.name || "UTC"}
                                                                                className="maintenance-warnings-date"
                                                                                customFormat={true}
                                                                                locale={locale}
                                                                                noWrap={false}
                                                                                oneControl={true}
                                                                            />
                                                                        }
                                                                    </span>
                                                                </div>
                                                                : <></>
                                                        }
                                                        {
                                                            (resolved && item?.nameResolvedby) ?
                                                                <div>
                                                                    <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['warnings_main_resolved_by']}:</label>
                                                                    <span>
                                                                        {item.nameResolvedby === "support" ? messages['warnings_main_device_support'] : item.nameResolvedby}
                                                                    </span>
                                                                </div>
                                                                : <></>
                                                        }
                                                        <div>
                                                            <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['warnings_main_occurrences']}:</label>
                                                            <span>
                                                                {item.eventCount}
                                                            </span>
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
                                                        <IconButton
                                                            onClick={() => {
                                                                dispatch(openWarningDetailsModal({
                                                                    unitId: data?.id,
                                                                    warning: item.warning,
                                                                    resolutionStatus: resolved,
                                                                    firstEventDate: item.firstEventDate,
                                                                    lastEventDate: item.lastEventDate
                                                                }))
                                                            }}
                                                            className="icon"
                                                            icon="arrow_forward_ios"
                                                            size="xsmall"
                                                            tooltip={messages['warnings_main_details']}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </div>
                                        )
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div style={{ marginLeft: 5 }}>
                        <Button variant='primary' onClick={() => handleClose()}>
                            {messages['warnings_main_close']}
                        </Button>
                    </div>
                </ModalFooter>
            </Modal >
            {openModalWarningDetails && <WarningDetails timezone={data?.timeZone?.name} />}
            {openModalResolveWarnings && <ResolveWarning />}
            {openModalConfirmResolveWarnings && <ConfirmResolve />}
            {openModalDeleteWarnings && <DeleteWarning />}
        </>
    )
}

export default Warnings;